/**
 * `T-39` · El papel que se pega a la mesa, como contrato.
 *
 * Lo que viaja entre la consola y la API cuando Enrique genera e imprime. Va
 * aparte de `codigo-de-qr.ts` a propósito: allí vive **cómo es un código**, que
 * lo comparten el navegador del comensal, la API y esto; aquí vive **qué se
 * pide y qué se devuelve** al imprimir, que sólo lo usan dos.
 *
 * ── La URL la arma la API, y por eso viaja ──────────────────────────────────
 * Cada papel trae su `url` completa. La consola **no** la construye pegando un
 * host a un código, y no es comodidad: el host de los QR lo conoce el
 * redirector, y si la consola lo supiera por su cuenta habría dos fuentes de
 * verdad sobre a dónde lleva el papel. La que se equivocara sería la que ya
 * está pegada a la mesa, y esa no se arregla con un despliegue.
 */
import { z } from 'zod';
/** Lo que dice un papel ya impreso. */
export const esquemaPapel = z.object({
    id: z.string(),
    /** Nulo cuando el papel es de la cafetería entera, sin sucursal. */
    branchId: z.string().nullable(),
    /** «Mesa 4», «Barra». Nulo cuando el papel es de la sucursal entera. */
    rotulo: z.string().nullable(),
    codigo: z.string(),
    /** Completa, tal como va impresa y tal como la lee la cámara. */
    url: z.string(),
});
/**
 * Lo mismo, más si **acaba de nacer**.
 *
 * Es la mitad visible de la idempotencia. Sin este campo, generar dos veces
 * devolvería la misma lista las dos veces y Enrique no tendría forma de
 * distinguir «se imprimieron cuatro papeles nuevos» de «estos cuatro ya
 * existían y no se tocó nada» — que es justo lo que el criterio de `T-39` pide
 * que se note.
 */
export const esquemaPapelGenerado = esquemaPapel.extend({ creado: z.boolean() });
/** El rótulo, con el mismo tope que el `check` de la tabla. */
const esquemaRotulo = z.string().trim().min(1).max(40);
export const esquemaGeneracionDePapeles = z.object({
    /** La sucursal, o nulo para una cafetería de un solo local. */
    branchId: z.string().nullable(),
    /**
     * Los rótulos que se quieren.
     *
     * **Vacío significa un papel para la sucursal entera**, que es el caso del
     * piloto, y está escrito así en vez de con un `boolean` aparte porque un
     * `{ porSucursal: true, rotulos: ['Mesa 1'] }` sería una petición con dos
     * respuestas posibles y ninguna forma de elegir.
     *
     * El tope de sesenta no es arbitrario: es más mesas de las que tiene una
     * cafetería de especialidad, y sin tope una petición pide diez mil códigos.
     */
    rotulos: z.array(esquemaRotulo).max(60),
}).strict().refine((g) => new Set(g.rotulos).size === g.rotulos.length, {
    message: 'Hay rótulos repetidos: dos papeles con el mismo rótulo serían la misma mesa contada dos veces.',
});
export const esquemaPapelesGenerados = z.object({
    papeles: z.array(esquemaPapelGenerado),
});
export const esquemaSucursal = z.object({ id: z.string(), nombre: z.string() });
/**
 * Lo que la pantalla necesita para pintarse entera de una sola petición: qué
 * sucursales hay y qué papeles ya existen.
 *
 * Los papeles vienen en **una lista plana** con su `branchId`, no agrupados por
 * sucursal. Agruparlos aquí obligaría a la API a decidir cómo se ve la pantalla,
 * y dejaría sin sitio a los papeles sin sucursal.
 */
export const esquemaPapelesDeCafeteria = z.object({
    nombre: z.string(),
    slug: z.string().nullable(),
    sucursales: z.array(esquemaSucursal),
    papeles: z.array(esquemaPapel),
});
//# sourceMappingURL=papeles-de-qr.js.map